import type { Action } from '@reduxjs/toolkit';
import { capitalize, get } from 'lodash';
import moment from 'moment';
import { Epic, combineEpics } from 'redux-observable';
import { Observable, forkJoin, of } from 'rxjs';
import { AjaxError } from 'rxjs-compat';
import { filter, switchMap } from 'rxjs/operators';

import type { OrgSubUnit, SubUnitType } from '@appTypes/esb';
import { selectedUnitProfileSel } from '@context/duck/selectorsTyped';
import {
  PACK_ROSTER_REQUEST,
  isValidOrganizationSel,
  packRosterRequest,
} from '@modules/advancement/common';
import {
  formatSubUnitFilters,
  retrieveStoredFilters,
  updateLocalStorage,
} from '@modules/advancement/packRoster/utilsTyped';
import {
  DenTypeId,
  getAddedAdults,
  getAddedDenChiefs,
  getAddedYouths,
} from '@modules/advancement/utilsTyped';
import { rosterApi } from '@modules/roster/api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { intl } from '@modules/shared';
import { toastService } from '@modules/toasts';
import { catchAndReport } from '@modules/utils/rxjs/operators';
import {
  organizationGuidSel,
  selectedOrganizationSel,
} from '@shared/duck/selectors/general.selectors';

import type { RootState } from '../../../../root/store';
import { subUnitTypes } from '../constants';
import {
  deleteSubUnitError,
  deleteSubUnitRequest,
  deleteSubUnitResponse,
  editSubUnitFailure,
  editSubUnitMembers,
  editSubUnitRequest,
  editSubUnitSuccess,
  getSubUnitsError,
  getSubUnitsResponse,
  hideAddToSubUnitModal,
  hideSetSubUnitModal,
  saveSubUnitFailure,
  saveSubUnitRequest,
  saveSubUnitSuccess,
} from './actions';
import {
  isDenSel,
  isSubUnitAllowedSel,
  selectedSubUnitSel,
  subUnitTypeSel,
  subUnitsInCacheSel,
} from './selectors';
import services from './services';

/**
 * The selected unit's sub-unit type, for the add, edit and delete flows: they
 * start from the sub-unit UI, which only packs and troops show
 * (`isSubUnitAllowedSel`), so the unit has one.
 */
const flowSubUnitType = (state: RootState) =>
  subUnitTypeSel(state) as SubUnitType;

const saveSubUnitEpic$: Epic<Action, Action, RootState> = (
  action$,
  state$,
  { store },
) =>
  action$.pipe(
    filter(saveSubUnitRequest.match),
    switchMap(
      ({
        payload: {
          denType,
          subUnitName,
          selectedAdults,
          selectedUsers,
          selectedDenChiefs,
        },
      }) => {
        const state = state$.value;

        const subUnitType = flowSubUnitType(state);
        const orgGuid = organizationGuidSel(state);
        const organizationProfile = selectedOrganizationSel(state);

        const isDen = subUnitType === subUnitTypes.DEN;

        const subUnitData = {
          subUnitName,
          subUnitApproved: true,
          forumEnabled: false,
          ...(isDen
            ? {
                denType,
                showDlEvents: true,
              }
            : {}),
        };

        return services
          .createSubUnit$({
            organizationGuid: orgGuid,
            subUnitType,
            subUnitData,
            dispatch: store.dispatch,
          })
          .pipe(
            switchMap(response => {
              const createdSubUnitData: OrgSubUnit = {
                subUnitId: response.id,
                subUnitName: subUnitData.subUnitName,
                denTypeId: isDen ? DenTypeId[subUnitData.denType ?? ''] : null,
                denType: isDen ? subUnitData.denType ?? '' : null,
                isApproved: subUnitData.subUnitApproved,
                isForumEnabled: subUnitData.forumEnabled,
                showDlEvents: isDen || null,
                unitId: Number(organizationProfile.unitId),
                dateCreated: moment().format('YYYY-MM-DD'),
              };

              const denChiefObservers = selectedDenChiefs.length
                ? selectedDenChiefs.map(selectedDenChief =>
                    services.addSubUnitDenChiefs$(
                      orgGuid,
                      {
                        ...selectedDenChief,
                        packId: +organizationProfile.unitId,
                        denId: [response.id],
                      },
                      store.dispatch,
                    ),
                  )
                : [of({ message: '' })];

              return forkJoin([
                selectedUsers.length
                  ? services.addSubUnitYouths$(
                      orgGuid,
                      subUnitType,
                      response.id,
                      selectedUsers,
                      store.dispatch,
                    )
                  : of({ message: '' }),
                selectedAdults.length
                  ? services.addSubUnitAdults$(
                      orgGuid,
                      subUnitType,
                      response.id,
                      selectedAdults,
                      store.dispatch,
                    )
                  : of({ message: '' }),
                ...denChiefObservers,
                of(createdSubUnitData),
              ]);
            }),
            switchMap(response => {
              const createdSubUnit = response[
                response.length - 1
              ] as OrgSubUnit;
              toastService.success(
                intl.formatMessage(
                  { id: 'subUnits.create.success' },
                  {
                    subUnitType: capitalize(subUnitType),
                    subUnitName,
                  },
                ),
              );
              const addedYouths = getAddedYouths(
                selectedUsers,
                createdSubUnit,
                isDen,
              );
              const addedAdults = getAddedAdults(
                selectedAdults,
                createdSubUnit,
                isDen,
              );
              const addedDenChiefs = isDen
                ? getAddedDenChiefs(selectedDenChiefs, createdSubUnit, isDen)
                : [];

              const retrievedStoredFilters = retrieveStoredFilters();
              if (retrievedStoredFilters[orgGuid]) {
                const formattedSubFilterTag =
                  formatSubUnitFilters(createdSubUnit);
                const updatedSubUnitsFilter =
                  retrievedStoredFilters[orgGuid].subUnitFilterTags;
                updatedSubUnitsFilter.push(formattedSubFilterTag);
                retrievedStoredFilters[orgGuid].subUnitFilterTags =
                  updatedSubUnitsFilter;
                updateLocalStorage(retrievedStoredFilters);
              }

              return of(
                hideSetSubUnitModal(),
                saveSubUnitSuccess({ subUnit: createdSubUnit, orgGuid }),
                editSubUnitMembers({
                  subUnit: createdSubUnit,
                  addedYouths,
                  addedAdults,
                  addedDenChiefs,
                }),
              );
            }),
            catchAndReport(err => {
              const errorMessage = get(err, ['data', 'errorDesc']);
              toastService.error(errorMessage);
              return of(
                saveSubUnitFailure(err),
                hideSetSubUnitModal(),
                packRosterRequest({ forceRefetch: true }),
              );
            }),
          );
      },
    ),
  );

const editSubUnitEpic$: Epic<Action, Action, RootState> = (
  action$,
  state$,
  { store },
) =>
  action$.pipe(
    filter(editSubUnitRequest.match),
    switchMap(
      ({
        payload: {
          denType,
          denChiefsToDelete = [],
          membersToDelete = [],
          selectedAdults = [],
          selectedUsers = [],
          subUnitId,
          subUnitName,
          selectedDenChiefs,
        },
      }) => {
        const state = state$.value;
        const isDen = isDenSel(state);
        const subUnitType = flowSubUnitType(state);
        const orgGuid = organizationGuidSel(state);
        const selectedSubUnit = selectedSubUnitSel(state);
        const organizationProfile = selectedOrganizationSel(state);

        const subUnitData = {
          subUnitName,
          ...(isDen ? { denType } : {}),
        };

        const denChiefObservers = selectedDenChiefs.length
          ? selectedDenChiefs.map(selectedDenChief =>
              services.addSubUnitDenChiefs$(
                orgGuid,
                {
                  ...selectedDenChief,
                  packId: +organizationProfile.unitId,
                  denId: [selectedSubUnit?.subUnitId ?? subUnitId],
                },
                store.dispatch,
              ),
            )
          : [of({ message: '' })];

        const removeDenChiefObservers = denChiefsToDelete.length
          ? denChiefsToDelete.map(selectedDenChief =>
              services.removeSubUnitDenChief$(
                orgGuid,
                selectedDenChief,
                store.dispatch,
              ),
            )
          : [of({ message: '' })];

        return forkJoin([
          services.editSubUnit$({
            organizationGuid: orgGuid,
            subUnitType,
            subUnitId,
            subUnitData,
            dispatch: store.dispatch,
          }),
          selectedUsers.length
            ? services.addSubUnitYouths$(
                orgGuid,
                subUnitType,
                subUnitId,
                selectedUsers,
                store.dispatch,
              )
            : of({}),
          selectedAdults.length
            ? services.addSubUnitAdults$(
                orgGuid,
                subUnitType,
                subUnitId,
                selectedAdults,
                store.dispatch,
              )
            : of({}),
          ...denChiefObservers,
          ...removeDenChiefObservers,
          membersToDelete.length
            ? services.deleteSubUnitUsers$(
                orgGuid,
                subUnitType,
                subUnitId,
                membersToDelete,
                store.dispatch,
              )
            : of({}),
        ]).pipe(
          switchMap(() => {
            toastService.success(
              intl.formatMessage(
                { id: 'subUnits.edit.success' },
                {
                  subUnitType: capitalize(subUnitType),
                  subUnitName,
                },
              ),
            );

            const editedSubUnitData = {
              subUnitId: selectedSubUnit?.subUnitId ?? subUnitId,
              subUnitName: subUnitData.subUnitName,
              denTypeId: isDen ? DenTypeId[subUnitData.denType ?? ''] : null,
              denType: isDen ? denType ?? '' : null,
              isApproved: selectedSubUnit?.isApproved ?? true,
              isForumEnabled: selectedSubUnit?.isForumEnabled ?? false,
              showDlEvents: isDen || null,
              unitId: selectedSubUnit?.unitId ?? 0,
              dateCreated: selectedSubUnit?.dateCreated ?? '',
            };

            const changedName = !!(
              selectedSubUnit && subUnitName !== selectedSubUnit.subUnitName
            );

            const addedYouths = getAddedYouths(
              selectedUsers,
              editedSubUnitData,
              isDen,
            );
            const addedAdults = getAddedAdults(
              selectedAdults,
              editedSubUnitData,
              isDen,
            );
            const addedDenChiefs = isDen
              ? getAddedDenChiefs(selectedDenChiefs, editedSubUnitData, isDen)
              : [];

            const retrievedStoredFilters = retrieveStoredFilters();

            if (retrievedStoredFilters[orgGuid]) {
              const formattedSubFilterTag =
                formatSubUnitFilters(editedSubUnitData);
              const updatedSubUnitsFilter = retrievedStoredFilters[
                orgGuid
              ].subUnitFilterTags.map(subFilterTag => {
                if (subFilterTag.id === formattedSubFilterTag.id) {
                  return formattedSubFilterTag;
                }
                return subFilterTag;
              });
              retrievedStoredFilters[orgGuid].subUnitFilterTags =
                updatedSubUnitsFilter;
              updateLocalStorage(retrievedStoredFilters);
            }

            return of(
              hideSetSubUnitModal(),
              hideAddToSubUnitModal(),
              editSubUnitSuccess({ subUnit: editedSubUnitData, orgGuid }),
              rosterApi.util.invalidateTags([
                tagFactory.UnitRoster.organizationRosters(orgGuid),
              ]),
              editSubUnitMembers({
                subUnit: editedSubUnitData,
                addedYouths,
                addedAdults,
                addedDenChiefs,
                deletedMembers: [...membersToDelete, ...denChiefsToDelete],
                changedName,
              }),
            );
          }),
          catchAndReport(err => {
            const errorMessage = get(err, ['data', 'errorDesc']);
            toastService.error(errorMessage);

            return of(
              editSubUnitFailure(err),
              hideSetSubUnitModal(),
              packRosterRequest({ forceRefetch: true }),
            );
          }),
        );
      },
    ),
  );

const deleteSubUnitEpic$: Epic<Action, Action, RootState> = (
  action$,
  state$,
  { store },
) =>
  action$.pipe(
    filter(deleteSubUnitRequest.match),
    switchMap(({ payload: subUnitId }) => {
      const state = state$.value;
      const subUnitType = flowSubUnitType(state);
      const orgGuid = organizationGuidSel(state);
      return services
        .deleteSubUnit$(orgGuid, subUnitType, subUnitId, store.dispatch)
        .pipe(
          switchMap(() => {
            toastService.success(
              intl.formatMessage(
                { id: 'subUnits.delete.success' },
                { subUnitType: capitalize(subUnitType) },
              ),
            );
            const retrievedStoredFilters = retrieveStoredFilters();
            if (retrievedStoredFilters[orgGuid]) {
              const updatedSubUnitsFilter = retrievedStoredFilters[
                orgGuid
              ].subUnitFilterTags.filter(tag => tag.id !== subUnitId);
              retrievedStoredFilters[orgGuid].subUnitFilterTags =
                updatedSubUnitsFilter;
              updateLocalStorage(retrievedStoredFilters);
            }
            return of(
              deleteSubUnitResponse({
                orgGuid,
                subUnitId,
                isDen: subUnitType === 'den',
              }),
            );
          }),
          catchAndReport(err => {
            const { status } = err as AjaxError;
            if (status === 401) {
              toastService.dismiss(undefined);
              toastService.error(
                intl.formatMessage({ id: 'subUnits.delete.fail.notAllowed' }),
              );
            }
            const errorMessage = get(err, ['data', 'errorDesc']);
            toastService.error(errorMessage);
            return of(deleteSubUnitError(err));
          }),
        );
    }),
  );

const loadSubUnitsEpic$: Epic<Action, Action, RootState> = (
  action$,
  state$,
  { store },
) =>
  action$
    .ofType(PACK_ROSTER_REQUEST)
    .filter(() => isValidOrganizationSel(state$.value))
    .filter(() => isSubUnitAllowedSel(state$.value))
    .switchMap(payload => {
      // @ts-expect-error forceRefetch will be removed when roster gets moved fully to RTK
      const { forceRefetch } = payload;
      const state = state$.value;
      const orgGuid = organizationGuidSel(state);
      const selectedUnitProfile = selectedUnitProfileSel(state);
      const isActive = get(selectedUnitProfile, 'charter.isActive', true);

      if (!isActive) {
        return of(getSubUnitsResponse({ orgGuid, subUnits: [] }));
      }

      const subUnitsInCache = subUnitsInCacheSel(state);
      if (subUnitsInCache && !forceRefetch) {
        return of(getSubUnitsResponse({ orgGuid, subUnits: subUnitsInCache }));
      }

      return services.getSubUnits$(orgGuid, forceRefetch, store.dispatch).pipe(
        switchMap((subUnits: OrgSubUnit[]) =>
          of(getSubUnitsResponse({ orgGuid, subUnits })),
        ),
        catchAndReport(err => {
          const errorMessage = get(err, ['data', 'errorDesc']);
          toastService.error(errorMessage);
          return Observable.of(getSubUnitsError({ orgGuid }));
        }),
      );
    });

export default combineEpics(
  saveSubUnitEpic$,
  editSubUnitEpic$,
  loadSubUnitsEpic$,
  deleteSubUnitEpic$,
);
